<template>
  <div
    class="overflow-hidden bg-white rounded-lg shadow-md transition-shadow duration-300 dark:bg-gray-700 hover:shadow-lg">
    <div class="p-6">
      <!-- Product Image -->
      <div class="overflow-hidden relative mb-4 w-full rounded-lg aspect-square">
        <img
          v-if="product.image_url"
          :src="product.image_url"
          :alt="product.name"
          class="object-cover w-full h-full" />
        <div
          v-else
          class="flex justify-center items-center w-full h-full bg-gray-200 dark:bg-gray-600">
          <span class="text-4xl font-semibold text-gray-400 dark:text-gray-300">
            {{ getInitials(product.name) }}
          </span>
        </div>
      </div>

      <h4 class="mb-2 text-lg font-semibold">{{ product.name }}</h4>
      <div class="mb-4">
        <p class="text-gray-600 dark:text-gray-300">
          {{ truncatedDescription }}
        </p>
        <button @click="seeMore = !seeMore" class="text-sm text-blue-500 cursor-pointer"
          v-if="product?.description?.length > 100">{{seeMore ? 'See Less' : 'See More'}}</button>
      </div>
      <div class="flex justify-between items-center">
        <span class="text-lg font-bold text-gray-900 dark:text-gray-100">
          {{ nairaSymbol }}{{ product.price }}
        </span>
        <span
          :class="[
            'px-2 py-1 text-xs font-semibold rounded-full',
            product.is_available
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          ]">
          {{ product.is_available ? 'Available' : 'Out of Stock' }}
        </span>
      </div>
      <div v-if="product.is_available && user && user.role === USER_ROLES.CUSTOMER && business.is_active" class="mt-4">
        <div class="flex items-center space-x-4">
          <PrimaryButton
            @click="decreaseQuantity"
            variant="secondary"
            size="small"
            :disabled="!quantity">
            <MinusIcon class="w-4 h-4" />
          </PrimaryButton>
          <span class="text-lg font-semibold">{{ quantity }}</span>
          <PrimaryButton
            @click="increaseQuantity"
            variant="secondary"
            size="small">
            <PlusIcon class="w-4 h-4" />
          </PrimaryButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { PlusIcon, MinusIcon } from '@heroicons/vue/24/solid'
import PrimaryButton from '@/components/general/PrimaryButton.vue'
import { computed, ref } from 'vue'
import { nairaSymbol } from '@/constants'
import { getInitials } from '@/utils'
import { USER_ROLES } from '@/types'
import { useAuthStore } from '@/stores/authStore'
import { storeToRefs } from 'pinia'

const authStore = useAuthStore()
const { user } = storeToRefs(authStore)
const seeMore = ref(false)
const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  modelValue: {
    type: Number,
    default: 0
  },
  business: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['update:modelValue'])

const quantity = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const increaseQuantity = () => {
  quantity.value++
}

const decreaseQuantity = () => {
  if (quantity.value > 0) {
    quantity.value--
  }
}

const truncatedDescription = computed(() => {
  if(props.product.description && props.product.description.length > 100 && !seeMore.value) {
    return props.product.description.substring(0, 100) + '...'
  }
  return props.product.description || ''
})
</script>
